import {
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { BehaviorSubject, Subject, Subscription, combineLatest } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { DialogPanelComponentContent } from '../../models/dialogs/DialogPanelComponentContent';
import { AppState } from '../../interfaces/app-state.interface';
import { OperationalLimitAction } from '../../state-management/operational-limit/operational-limit.action';
import { TrackingActions } from '../../state-management/tracking/tracking.action';
import { ActionEventsEnum } from '../../enum/tracking/action-events.enum';
import { FinancePosition } from '../../models/pos-risk/finance-position';
import { GetUserAcceptanceTerm } from '../../interfaces/operational-limit/operational-limit.interface';
import { Router } from '@angular/router';
import { Token } from '../../models/authentication/token.model';
import {
  BankAccountOwnershipService,
  IOwnershipValidationPayload,
  OwnershipInvalidError,
} from '../../services/banks/bank-account-ownership.service';
import { IAccount, mapBankAccounts } from '../../helpers/banks/bank-account.util';

export { IAccount } from '../../helpers/banks/bank-account.util';

enum HubLoIntent {
  Authorize = 'authorize',
  Revoke = 'revoke',
  DeleteAccount = 'delete-account',
}

interface IHubLoDialogData {
  intent?: HubLoIntent;
  accounts?: IAccount[];
  token?: Token;
  origin?: 'Home' | 'Dados bancários'
}

export enum HubLoStep {
  AccountSelector = 'account-selector',
  AuthorizationIntro = 'authorization-intro',
  AuthorizationResult = 'authorization-result',
  Revoke = 'revoke-result',
  RevokeConfirmation = 'revoke-confirmation',
  DeleteBank = 'delete-bank',
  DeleteAccount = 'delete-account',
}

export enum HubLoAuthorizeResult {
  Success = 'success',
  Failure = 'failure',
  OwnershipInvalid = 'ownership-invalid',
}

@AutoUnsubscribe({ blackList: ['step$', 'selectedAccount$', 'result$', 'consent$', 'isProcessing$', 'destroy$'] })
@Component({
  selector: 'hub-lo',
  templateUrl: './hub-lo.component.html',
  styleUrls: ['./hub-lo.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HubLoComponent
  extends DialogPanelComponentContent
  implements OnInit, OnDestroy
{
  public readonly steps = HubLoStep;

  public readonly step$ = new BehaviorSubject<HubLoStep>(
    HubLoStep.AuthorizationIntro,
  );
  public readonly selectedAccount$ = new BehaviorSubject<IAccount | null>(null);
  public readonly result$ = new BehaviorSubject<HubLoAuthorizeResult | null>(
    null,
  );
  public readonly consent$ = new BehaviorSubject<boolean>(false);
  public readonly isProcessing$ = new BehaviorSubject<boolean>(false);
  public processingText = 'Verificando a titularidade...';
  public revokeError = false;
  private subscription: Subscription;
  private ownershipSubscription: Subscription;

  private readonly destroy$ = new Subject<void>();
  public accounts: IAccount[];
  private readonly intent: HubLoIntent;
  private readonly santanderBankId = 2;
  private cblc: string;
  private documentNumber: string;
  private readonly santanderBankCode = 33;
  private operationalLimit: GetUserAcceptanceTerm;
  private financialPending: boolean | null = null;
  public origin: 'Home' | 'Dados bancários';
  public token: Token;
  private previousHtmlOverflow: string | null = null;
  private previousBodyOverflow: string | null = null;

  constructor(
    elementRef: ElementRef,
    private dialogRef: MatDialogRef<HubLoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IHubLoDialogData,
    private store: Store<AppState>,
    private router: Router,
    private ownershipService: BankAccountOwnershipService,
    private actions$: Actions,
    private ngZone: NgZone,
  ) {
    super(elementRef, true);
    this.dialogRef.addPanelClass('hub-lo-panel');

    this.intent = data?.intent || HubLoIntent.Authorize;
    this.accounts = data?.accounts || [];
    this.token = data?.token || null;
    this.origin = data?.origin || 'Home';
    
    this.subscription = combineLatest([
      this.store.select((state) => state.authentication),
      this.store.select(({ finance }) => finance.finance),
      this.store.select(({ operationalLimit }) => operationalLimit),
      this.store.select((state) => state.userProfile?.user?.cpfcnpj),
    ]).subscribe(([auth, finance, operationalLimit, cpfcnpj]) => {
      this.cblc = auth.currentUser.cblc;
      this.financialPending = this.hasFinancialPending(finance);
      this.operationalLimit = operationalLimit.value;
      this.documentNumber = cpfcnpj || this.documentNumber;
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.lockBackgroundScroll();
    if (!this.accounts.length) {
      this.loadAccountsFromStore();
    } else {
      this.initializeFlow();
    }
    this.loadOperationalLimitTerms();
  }

  private lockBackgroundScroll(): void {
    this.previousHtmlOverflow = document.documentElement.style.overflow;
    this.previousBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  private unlockBackgroundScroll(): void {
    document.documentElement.style.overflow = this.previousHtmlOverflow || '';
    document.body.style.overflow = this.previousBodyOverflow || '';
  }

  ngOnDestroy(): void {
    this.unlockBackgroundScroll();
    this.destroy$.next();
    this.destroy$.complete();
    this.step$.complete();
    this.selectedAccount$.complete();
    this.result$.complete();
    this.consent$.complete();
    this.isProcessing$.complete();
    if (this.ownershipSubscription) {
      this.ownershipSubscription.unsubscribe();
    }
  }

  public chooseAccount(accountId: number): void {
    const account = this.accounts.find((item) => item.id === accountId) || null;
    this.selectedAccount$.next(account);
    this.setConsent(false);
  }

  public closeDrawer = (): void => {
    this.close(this.dialogRef, this.result$.value);
  };

  public proceedAuthorizationIntro(): void {
    if (!this.accounts.length) {
      this.goToRegisterAccount();
      return;
    }
    if (this.accounts.length > 1) {
      this.step$.next(HubLoStep.AccountSelector);
    } else {
      this.proceedAuthorizationAccountSelector();
    }
  }

  public proceedAuthorizationAccountSelector(): void {
    const account = this.selectedAccount$.value;
    if (!account || !account.id || this.isProcessing$.value) {
      return;
    }
    const payload = this.buildOwnershipPayload(account);
    if (!payload.documentNumber || !account.number || !payload.branch) {
      this.result$.next(HubLoAuthorizeResult.Failure);
      this.step$.next(HubLoStep.AuthorizationResult);
      return;
    }
    this.processingText = 'Verificando a titularidade...';
    this.isProcessing$.next(true);
    this.ownershipSubscription = this.ownershipService
      .validateOwnership(payload)
      .subscribe({
        next: () => {
          this.dispatchAcceptTerm(account.id);
        },
        error: (error: unknown) => {
          const isOwnershipInvalid = error instanceof OwnershipInvalidError;
          this.isProcessing$.next(false);
          this.result$.next(
            isOwnershipInvalid
              ? HubLoAuthorizeResult.OwnershipInvalid
              : HubLoAuthorizeResult.Failure,
          );
          this.step$.next(HubLoStep.AuthorizationResult);
        },
      });
  }

  private dispatchAcceptTerm(accountId: number): void {
    this.processingText = 'Processando...';
    this.isProcessing$.next(true);
    this.actions$
      .pipe(
        ofType(
          OperationalLimitAction.acceptTermSuccess,
          OperationalLimitAction.acceptTermError,
        ),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((action: Action) => {
        const accepted = action.type === OperationalLimitAction.acceptTermSuccess;
        this.isProcessing$.next(false);
        if (accepted) {
          this.refreshAcceptanceTerm();
        }
        this.result$.next(
          accepted ? HubLoAuthorizeResult.Success : HubLoAuthorizeResult.Failure,
        );
        this.step$.next(HubLoStep.AuthorizationResult);
      });
    this.store.dispatch({
      type: OperationalLimitAction.acceptTerm,
      payload: {
        cblc: this.cblc,
        account: { AccountId: accountId },
      },
    });
  }

  private buildOwnershipPayload(account: IAccount): IOwnershipValidationPayload {
    const documentNumber = (this.documentNumber || '').replace(/\D/g, '');
    const accountNumberRaw = `${account.number || ''}${account.digit || ''}`;
    return {
      accountNumber: accountNumberRaw.padStart(12, '0'),
      bank: this.santanderBankCode,
      branch: Number(account.agency) || 0,
      documentNumber,
    };
  }

  public proceedRevoke(): void {
    if (this.financialPending === null || this.isProcessing$.value) {
      return;
    }
    this.trackLo(ActionEventsEnum.ELEMENT_CLICKED, 'Botão Revogar comunicação');
    if (this.financialPending) {
      this.trackLo(
        ActionEventsEnum.PAGE_LOADED,
        'Revogação não permitida (operações pendentes)',
      );
      this.step$.next(HubLoStep.RevokeConfirmation);
      return;
    }
    if (!this.cblc) {
      this.finishRevoke(false);
      return;
    }
    this.processingText = 'Processando...';
    this.isProcessing$.next(true);
    this.actions$
      .pipe(
        ofType(
          OperationalLimitAction.rejectTermSuccess,
          OperationalLimitAction.rejectTermError,
        ),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((action: Action) => {
        this.finishRevoke(action.type === OperationalLimitAction.rejectTermSuccess);
      });
    this.store.dispatch({
      type: OperationalLimitAction.rejectTerm,
      payload: { cblc: this.cblc },
    });
  }

  private finishRevoke(revoked: boolean): void {
    this.isProcessing$.next(false);
    this.revokeError = !revoked;
    if (revoked) {
      this.refreshAcceptanceTerm();
    }
    this.trackLo(
      ActionEventsEnum.PAGE_LOADED,
      revoked ? 'Revogação concluída' : 'Revogação não concluída (erro)',
    );
    this.step$.next(HubLoStep.RevokeConfirmation);
  }

  private refreshAcceptanceTerm(): void {
    this.store.dispatch({ type: OperationalLimitAction.getAcceptanceTerm });
  }

  /**
   * Metrica centralizada dos passos de LO da gaveta que nao possuem tracking
   * proprio (revogar e excluir conta). Usa o `origin` recebido na abertura.
   */
  private trackLo(event: string, element: string): void {
    this.store.dispatch({
      type: TrackingActions.trackEvents,
      payload: {
        type: event,
        events: {
          'Product Area Origin': this.origin,
          Step: 'Comunicação entre contas',
          Element: element,
        },
      },
    });
  }

  private initializeFlow(): void {
    if (this.accounts.length === 1) {
      this.selectedAccount$.next(this.accounts[0]);
      this.setConsent(false);
    }
    if (this.intent === HubLoIntent.Revoke) {
      this.trackLo(ActionEventsEnum.PAGE_LOADED, 'Revogar comunicação entre contas');
    } else if (this.intent === HubLoIntent.DeleteAccount) {
      this.trackLo(ActionEventsEnum.PAGE_LOADED, 'Excluir conta com comunicação entre contas');
    }
    this.back();
  }

  private loadAccountsFromStore(): void {
    this.store
      .select((state) => state.userProfile?.user?.bankAccounts)
      .pipe(takeUntil(this.destroy$))
      .subscribe((bankAccounts) => {
        if (!bankAccounts || !bankAccounts.length) {
          return;
        }
        this.accounts = mapBankAccounts(bankAccounts).filter(
          (account) => account.bankId === this.santanderBankId,
        );
        this.initializeFlow();
      });
  }

  private loadOperationalLimitTerms(): void {
    this.store
      .select((state) => state.operationalLimit)
      .pipe(
        filter((operationalLimit) => !operationalLimit.pending),
        takeUntil(this.destroy$),
      )
      .subscribe((opLimit) => {
        this.operationalLimit = opLimit.value;
      });
  }

  public setConsent(consent: boolean): void {
    this.consent$.next(!!consent);
  }

  public back(): void {
    switch (this.intent) {
      case HubLoIntent.Authorize: {
        this.step$.next(HubLoStep.AuthorizationIntro);
        return;
      }
      case HubLoIntent.DeleteAccount: {
        this.step$.next(HubLoStep.DeleteAccount);
        return;
      }
      case HubLoIntent.Revoke: {
        this.step$.next(HubLoStep.Revoke);
        return;
      }
      default: {
        this.step$.next(HubLoStep.AuthorizationIntro);
        return;
      }
    }
  };

  public redirect(url: string): void {
    this.closeAndNavigate(url);
  }

  public goToRegisterAccount(): void {
    this.closeAndNavigate('/minha-conta/dados-bancarios/edit?type=lo');
  }

  private closeAndNavigate(url: string): void {
    this.close(this.dialogRef, this.result$.value);
    this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      this.ngZone.run(() => this.router.navigateByUrl(url));
    });
  }

  public hasFinancialPending(finance: FinancePosition): boolean {
    return (
      finance.projectedD1 + finance.balanceD0 > 0 &&
      finance.balanceUsedToValidateStock > 0
    );
  }
}
